import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'

export default function DeckContextMenu({ deckId, onViewQuiz, onManageThemes, onEdit, onDelete, onDuplicate, showLabel = false, hideViewQuiz = false, readOnly = false, label = 'Gérer' }) {
  const [open, setOpen] = useState(false)
  const [showShare, setShowShare] = useState(false)
  const [copied, setCopied] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 })
  const btnRef = useRef(null)
  const menuRef = useRef(null)
  const shareRef = useRef(null)
  const shareUrl = `${window.location.origin}/share/${deckId}`

  useEffect(() => {
    if (!open && !showShare) return
    function handleClickOutside(e) {
      const inBtn = btnRef.current?.contains(e.target)
      const inMenu = menuRef.current?.contains(e.target)
      const inShare = shareRef.current?.contains(e.target)
      if (!inBtn && !inMenu && !inShare) {
        setOpen(false)
        setShowShare(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, showShare])

  function handleToggle(e) {
    e.stopPropagation()
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right })
    }
    setOpen(o => !o)
    setShowShare(false)
  }

  function handleAction(e, fn) {
    e.stopPropagation()
    setOpen(false)
    setShowShare(false)
    fn?.()
  }

  function handleShare(e) {
    e.stopPropagation()
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right })
    }
    setOpen(false)
    setShowShare(true)
  }

  async function handleCopy(e) {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(shareUrl)
    } catch {
      const el = document.createElement('textarea')
      el.value = shareUrl
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    }
    setCopied(true)
    setTimeout(() => { setCopied(false); setShowShare(false) }, 2000)
  }

  const menuItems = readOnly
    ? [
        { icon: '📋', label: 'Dupliquer le quiz', action: e => handleAction(e, onDuplicate) },
        { icon: '📤', label: 'Partager', action: handleShare },
      ]
    : [
        !hideViewQuiz && { icon: '👁', label: 'Voir le quiz', action: e => handleAction(e, onViewQuiz) },
        { icon: '📋', label: 'Dupliquer le quiz', action: e => handleAction(e, onDuplicate) },
        { icon: '📂', label: 'Gérer les thèmes', action: e => handleAction(e, onManageThemes) },
        { icon: '📤', label: 'Partager', action: handleShare },
        { icon: '✏️', label: 'Modifier', action: e => handleAction(e, onEdit) },
        { icon: '🗑', label: 'Supprimer', action: e => handleAction(e, onDelete), danger: true },
      ].filter(Boolean)

  return (
    <>
      {showLabel ? (
        <button
          ref={btnRef}
          type="button"
          onClick={handleToggle}
          className="btn-dark px-4 py-2 flex items-center gap-1.5 text-sm"
        >
          <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
          {label}
        </button>
      ) : (
        <button
          ref={btnRef}
          type="button"
          onClick={handleToggle}
          title="Gérer le quiz"
          className="w-8 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
      )}

      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
          className="w-52 bg-gray-800 border border-white/15 rounded-xl shadow-2xl py-1 overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {menuItems.map(item => (
            <button
              key={item.label}
              type="button"
              onClick={item.action}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition hover:bg-white/10 ${
                item.danger ? 'text-red-400 hover:text-red-300' : 'text-white'
              }`}
            >
              <span className="w-5 text-center">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )}

      {showShare && createPortal(
        <div
          ref={shareRef}
          style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
          className="w-64 bg-gray-800 border border-white/15 rounded-xl shadow-2xl p-3 space-y-2"
          onClick={e => e.stopPropagation()}
        >
          <p className="text-white/60 text-xs font-medium">Lien de partage</p>
          <div className="bg-gray-900/60 border border-white/10 rounded-lg px-2.5 py-1.5">
            <span className="text-white/50 text-xs font-mono break-all">{shareUrl}</span>
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition ${
              copied
                ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                : 'bg-primary/90 hover:bg-primary text-white'
            }`}
          >
            {copied ? '✅ Lien copié !' : '📋 Copier le lien'}
          </button>
        </div>,
        document.body
      )}
    </>
  )
}
